import { styled, useTheme } from "@mui/material/styles";
import { useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import { CompanyDataGrid } from "../../../components/DataGrid";
import {
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import {
  useCompanyList,
  getErrorMessage,
} from "@/features/commercialBook/hooks/useCompanyList";
import { useCompanyListBulk } from "@/features/commercialBook/api";
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { type CheckedCompanyDetail } from "@/features/commercialBook/routes/CompanyList";
import { type FetchParams } from "@/features/commercialBook/types";

interface CompanyListDataGridRow {
  id: number;
  corporateNumber: string;
  companyName: string;
  location: string;
  closed: boolean;
}

interface FeedGridProps {
  checkedCompanyDetails: CheckedCompanyDetail[];
  setCheckedCompanyDetails: React.Dispatch<
    React.SetStateAction<CheckedCompanyDetail[]>
  >;
  fetchParams: FetchParams;
}

const StyledDataGrid = styled(CompanyDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

const makeGridColDef = (
  checkedCompanyDetails: CheckedCompanyDetail[],
  setCheckedCompanyDetails: React.Dispatch<
    React.SetStateAction<CheckedCompanyDetail[]>
  >,
  selectAllCompanyDetail: boolean,
  setSelectAllCompanyDetail: React.Dispatch<React.SetStateAction<boolean>>,
  rows: CompanyListDataGridRow[]
): GridColDef[] => {
  const handleCheckboxChange = (companyDetail: CheckedCompanyDetail): void => {
    const currentIndex = checkedCompanyDetails.findIndex(
      (item) =>
        item.corporateNumber === companyDetail.corporateNumber &&
        item.companyName === companyDetail.companyName &&
        item.location === companyDetail.location &&
        item.closed === companyDetail.closed
    );
    const newChecked = [...checkedCompanyDetails];

    if (currentIndex === -1) {
      newChecked.push(companyDetail);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setSelectAllCompanyDetail(false);
    setCheckedCompanyDetails(newChecked);
  };

  const handleSelectAllCompanyInfoChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (event.target.checked) {
      const allCompanyDetails = rows.map((row) => ({
        corporateNumber: row.corporateNumber,
        location: row.location,
        companyName: row.companyName,
        closed: row.closed,
      }));
      setCheckedCompanyDetails(allCompanyDetails);
    } else {
      setCheckedCompanyDetails([]);
    }
    setSelectAllCompanyDetail(event.target.checked);
  };

  return [
    {
      field: "checkbox",
      headerName: "",
      width: 70,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<CompanyListDataGridRow>) => (
        <Checkbox
          onChange={() => {
            const { corporateNumber, location, companyName, closed } =
              params.row as CompanyListDataGridRow;

            const companyDetail: CheckedCompanyDetail = {
              corporateNumber,
              location,
              companyName,
              closed,
            };
            handleCheckboxChange(companyDetail);
          }}
          checked={checkedCompanyDetails.some(
            (detail) =>
              detail.corporateNumber ===
                (params.row as CompanyListDataGridRow).corporateNumber &&
              detail.companyName ===
                (params.row as CompanyListDataGridRow).companyName &&
              detail.location ===
                (params.row as CompanyListDataGridRow).location &&
              detail.closed === (params.row as CompanyListDataGridRow).closed
          )}
        />
      ),
      renderHeader: () => {
        return (
          <Checkbox
            checked={selectAllCompanyDetail}
            onChange={handleSelectAllCompanyInfoChange}
          />
        );
      },
    },
    {
      field: "id",
      headerName: "No",
      width: 40,
      sortable: false,
      renderCell: (params: GridRenderCellParams<CompanyListDataGridRow>) => {
        return Number(params.id) + 1;
      },
    },
    {
      field: "corporateNumber",
      headerName: "会社法人等番号",
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<CompanyListDataGridRow>) => {
        const { corporateNumber, closed } =
          params.row as CompanyListDataGridRow;
        return (
          <div>
            {corporateNumber}
            {closed && (
              <Typography variant="caption" display="block">
                (閉鎖登記簿)
              </Typography>
            )}
          </div>
        );
      },
    },
    {
      field: "companyName",
      headerName: "商号・名称",
      width: 500,
      sortable: false,
    },
    {
      field: "location",
      headerName: "所在地",
      width: 500,
      sortable: false,
    },
  ];
};

/** 選択済みリスト内で対象の会社が一致するかを判定する（テーブル・カード共通の同一性ルール） */
const isCompanyChecked = (
  checkedCompanyDetails: CheckedCompanyDetail[],
  company: CheckedCompanyDetail
): boolean =>
  checkedCompanyDetails.some(
    (detail) =>
      detail.corporateNumber === company.corporateNumber &&
      detail.companyName === company.companyName &&
      detail.location === company.location &&
      detail.closed === company.closed
  );

/** カード内の1項目（ラベル＋値）を横並びで表示する */
const CardRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 1.5,
      py: 0.25,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: "right", minWidth: 0, wordBreak: "break-all" }}>
      {children}
    </Box>
  </Box>
);

/**
 * SP（狭幅）向けの会社・法人カード一覧。DataGridは列が多く横スクロールになるため、
 * 1社=1カードで縦に並べ、取得対象の選択チェックを各カード内に置く。
 * 選択状態・全選択の扱いはテーブル版（makeGridColDef）と同じ。
 */
const MobileCompanyCards: React.FC<{
  rows: CompanyListDataGridRow[];
  checkedCompanyDetails: CheckedCompanyDetail[];
  setCheckedCompanyDetails: React.Dispatch<
    React.SetStateAction<CheckedCompanyDetail[]>
  >;
  isLoading: boolean;
}> = ({ rows, checkedCompanyDetails, setCheckedCompanyDetails, isLoading }) => {
  const toggleCompany = (company: CheckedCompanyDetail): void => {
    const currentIndex = checkedCompanyDetails.findIndex(
      (item) =>
        item.corporateNumber === company.corporateNumber &&
        item.companyName === company.companyName &&
        item.location === company.location &&
        item.closed === company.closed
    );
    const newChecked = [...checkedCompanyDetails];
    if (currentIndex === -1) {
      newChecked.push(company);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setCheckedCompanyDetails(newChecked);
  };

  const allChecked =
    rows.length > 0 && checkedCompanyDetails.length === rows.length;

  const handleSelectAll = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (event.target.checked) {
      setCheckedCompanyDetails(
        rows.map((row) => ({
          corporateNumber: row.corporateNumber,
          location: row.location,
          companyName: row.companyName,
          closed: row.closed,
        }))
      );
    } else {
      setCheckedCompanyDetails([]);
    }
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
        <Typography sx={{ mt: 2 }}>
          法人一覧を取得しています。しばらくお待ちください
        </Typography>
      </Stack>
    );
  }
  if (rows.length === 0) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <Typography>該当する会社・法人はありません</Typography>
      </Stack>
    );
  }
  return (
    <Stack spacing={1.5}>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={allChecked}
            indeterminate={!allChecked && checkedCompanyDetails.length > 0}
            onChange={handleSelectAll}
          />
        }
        label={<Typography variant="body2">すべて選択</Typography>}
        sx={{ ml: 0 }}
      />
      {rows.map((row, index) => {
        const company: CheckedCompanyDetail = {
          corporateNumber: row.corporateNumber,
          location: row.location,
          companyName: row.companyName,
          closed: row.closed,
        };
        const checked = isCompanyChecked(checkedCompanyDetails, company);
        return (
          <Card key={row.id} variant="outlined">
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {row.companyName || "（商号・名称なし）"}
                </Typography>
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={() => {
                    toggleCompany(company);
                  }}
                  sx={{ p: 0, flexShrink: 0 }}
                  inputProps={{ "aria-label": "取得対象に選択" }}
                />
              </Box>
              <CardRow label="No">
                <Typography variant="body2">{index + 1}</Typography>
              </CardRow>
              <CardRow label="会社法人等番号">
                <Typography variant="body2">
                  {row.corporateNumber || "—"}
                  {row.closed ? "（閉鎖登記簿）" : ""}
                </Typography>
              </CardRow>
              <CardRow label="所在地">
                <Typography variant="body2">{row.location || "—"}</Typography>
              </CardRow>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};

export const FeedGrid: React.FC<FeedGridProps> = ({
  checkedCompanyDetails,
  setCheckedCompanyDetails,
  fetchParams,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [selectAllCompanyDetail, setSelectAllCompanyDetail] =
    useState<boolean>(false);

  const isBulk = (fetchParams.corporateNumbers ?? []).length > 0;

  // API側と変数名を揃える
  const convertedFetchParams = isBulk
    ? null
    : {
        ...fetchParams,
        todouhuken: fetchParams.prefectureName,
        shyozai: fetchParams.location,
      };

  const { data: singleRows, isLoading: singleLoading } =
    useCompanyList(convertedFetchParams);
  const {
    data: bulkRows,
    isLoading: bulkLoading,
    bulkSearchIssue,
  } = useCompanyListBulk({
    corporateNumbers: isBulk ? fetchParams.corporateNumbers ?? [] : [],
    closed: fetchParams.closed,
    kubun: fetchParams.kubun,
  });

  const rows = isBulk ? bulkRows : singleRows;
  const isLoading = isBulk ? bulkLoading : singleLoading;

  const colDef = makeGridColDef(
    checkedCompanyDetails,
    setCheckedCompanyDetails,
    selectAllCompanyDetail,
    setSelectAllCompanyDetail,
    rows
  );

  return (
    <>
      {isMobile ? (
        <MobileCompanyCards
          rows={rows}
          checkedCompanyDetails={checkedCompanyDetails}
          setCheckedCompanyDetails={setCheckedCompanyDetails}
          isLoading={isLoading}
        />
      ) : (
        <StyledDataGrid
          stickyHeader={true}
          autoHeight
          disableColumnMenu
          disableSelectionOnClick={true}
          rows={rows}
          loading={isLoading}
          columns={colDef}
          sortModel={[
            {
              field: "id",
              sort: "asc",
            },
          ]}
        />
      )}
      {isBulk && bulkSearchIssue !== undefined && !isLoading && (
        <Alert
          severity={bulkSearchIssue.isPartialSuccess ? "warning" : "error"}
          sx={{ mt: 2 }}
        >
          <AlertTitle>
            {bulkSearchIssue.isPartialSuccess
              ? "一部の会社法人等番号の取得に失敗しました"
              : "一括検索でエラーが発生しました"}
          </AlertTitle>
          {bulkSearchIssue.detail !== "" && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {bulkSearchIssue.detail}
            </Typography>
          )}
          {bulkSearchIssue.failures.length > 0 && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
              {bulkSearchIssue.failures.map((failure) => {
                const displayName =
                  fetchParams.corporateNumberDisplayNames?.[
                    failure.corporateNumber
                  ]?.trim();
                const label =
                  displayName !== undefined && displayName !== ""
                    ? `${failure.corporateNumber}（${displayName}）`
                    : failure.corporateNumber;
                const reason =
                  getErrorMessage(failure.status) ??
                  `エラーが発生しました（status: ${failure.status}）`;
                return (
                  <Typography
                    key={failure.corporateNumber}
                    component="li"
                    variant="body2"
                  >
                    {label}: {reason}
                  </Typography>
                );
              })}
            </Box>
          )}
        </Alert>
      )}
      {!isMobile && isLoading && (
        <Typography sx={{ textAlign: "center", mt: 3 }}>
          法人一覧を取得しています。しばらくお待ちください
        </Typography>
      )}
    </>
  );
};
